import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import trim from 'lodash/trim'
import apiPost from 'lib/network/apiPost'
import notify from 'components/atoms/Notify'
import Button from 'components/atoms/Button'
import { buildShortLinkUrl } from 'utils/shortLink'

const CreateShortLinkModal = ({ onSuccess, close }) => {
  const { t, i18n } = useTranslation()
  const isRTL = i18n.language === 'ar'

  const [destinationUrl, setDestinationUrl] = useState('')
  const [label, setLabel] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [createdShortUrl, setCreatedShortUrl] = useState(null)

  const copyText = async text => {
    try {
      await navigator.clipboard.writeText(text)
      notify.success(t('shortLinks.copiedShortUrl'))
    } catch {
      notify.error(t('shortLinks.copyFailed'))
    }
  }

  const handleSubmit = async e => {
    e.preventDefault()
    const dest = trim(destinationUrl)
    if (!dest) {
      notify.error(t('shortLinks.destinationRequired'))
      return
    }
    setSubmitting(true)
    const response = await apiPost('short-link', {
      destinationUrl: dest,
      label: trim(label),
    })
    setSubmitting(false)
    if (response?.success) {
      notify.success(t('shortLinks.createdSuccess'))
      const created = response?.data?.shortLink
      const su = created?.shortUrl || buildShortLinkUrl(created?.slug)
      setCreatedShortUrl(su || null)
      onSuccess?.()
    }
  }

  const handleClose = () => {
    setDestinationUrl('')
    setLabel('')
    setCreatedShortUrl(null)
    close?.()
  }

  const startNewShortLink = () => {
    setCreatedShortUrl(null)
    setDestinationUrl('')
    setLabel('')
    setSubmitting(false)
  }

  if (createdShortUrl) {
    return (
      <div className="space-y-4">
        <p className="text-sm font-normal text-gray-700">{t('shortLinks.shortLinkReady')}</p>
        <a href={createdShortUrl} target="_blank" rel="noopener noreferrer" className="block text-sm text-blue-600 hover:underline break-all font-mono">
          {createdShortUrl}
        </a>
        <div className="flex flex-wrap gap-2 justify-end">
          <Button type="button" variant="outline" size="sm" onClick={() => copyText(createdShortUrl)}>
            {t('shortLinks.copyShortUrl')}
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={startNewShortLink}>
            {t('shortLinks.createNewShortLink')}
          </Button>
          <Button type="button" size="sm" onClick={handleClose}>
            {t('common.close')}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-5">
      <p className="text-sm text-gray-600">{t('shortLinks.shortUrlHint')}</p>
      <div className="grid grid-cols-1 gap-6">
        <div>
          <label htmlFor="shortlink-modal-destination" className="block text-sm font-normal text-gray-700 mb-1">
            {t('shortLinks.destinationUrl')}
          </label>
          <input
            id="shortlink-modal-destination"
            type="url"
            value={destinationUrl}
            onChange={e => setDestinationUrl(e.target.value)}
            className="w-full"
            placeholder="https://example.com/path?utm_source=email"
            dir="ltr"
          />
        </div>
        <div>
          <label htmlFor="shortlink-modal-label" className="block text-sm font-normal text-gray-700 mb-1">
            {t('shortLinks.labelOptional')}
          </label>
          <input
            id="shortlink-modal-label"
            type="text"
            value={label}
            onChange={e => setLabel(e.target.value)}
            className="w-full"
            placeholder={t('shortLinks.labelPlaceholder')}
            dir={isRTL ? 'rtl' : 'ltr'}
          />
        </div>
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <button type="button" className="inline-flex justify-center rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-gray-900 hover:bg-gray-200" onClick={handleClose}>
          {t('common.cancel')}
        </button>
        <button
          type="submit"
          disabled={submitting}
          className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm rounded-md text-white bg-dark-600 hover:bg-dark-700 disabled:opacity-60"
        >
          {submitting ? t('common.loading') : t('shortLinks.create')}
        </button>
      </div>
    </form>
  )
}

export default CreateShortLinkModal
